import React, { useCallback, useEffect, useState } from 'react';

import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import cx from 'classnames';
import {
  transcriptionAndSolutionDelete,
  transcriptionsAndSolutionsSendFeedback,
  transcriptionsAndSolutionsUpdate,
  useAssignmenTasks,
  useTranscriptionsAndSolutionsResults
} from 'sdk';

import { deltaToMarkdown } from 'pages/Teachers/shared/AssignmentDialog/parser';
import EditDialog from 'pages/Teachers/shared/TranscriptionsAndSolutionsEditDialog';
import QuestionField from 'pages/Teachers/shared/TranscriptionsAndSolutionsMarkdownField';
import { colors } from 'theme/palette';

import Typography from 'components/Typography';

import { ReactComponent as DeleteIcon } from './assets/delete-icon.svg';
import { ReactComponent as EditIcon } from './assets/edit-icon.svg';
import styles from './styles.module.scss';

const TrackerQuestionsAndAnswersList = ({ trackerId }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [editOpen, seteditOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(null);

  const [pages, setPages] = useState([]);
  const [groupedDataByPages, setGroupedDataByPages] = useState([]);

  const { data: assignmentPages } = useAssignmenTasks({
    assignmentId: trackerId
  });

  const [transcriptionsAndSolutionsData, setTranscriptionsAndSolutionsData] =
    useState(null);

  const {
    data,
    refetch: refetchTranscriptionsAndSolutions,
    isLoading: isLoadingTranscriptionsAndSolutions
  } = useTranscriptionsAndSolutionsResults({ trackerId });

  useEffect(() => {
    if (!data) return;
    const groupedData = data.reduce((acc, item) => {
      const { objective_id } = item;
      if (!acc[objective_id]) {
        acc[objective_id] = [];
      }
      acc[objective_id].push(item);
      return acc;
    }, {});

    setGroupedDataByPages(groupedData);
  }, [data]);

  useEffect(() => {
    if (assignmentPages?.length > 0) {
      setPages(assignmentPages);
      setCurrentPage(assignmentPages[0]);
    }
  }, [assignmentPages]);

  useEffect(() => {
    if (currentPage) {
      const currentPageData = groupedDataByPages[currentPage.id];
      if (currentPageData) {
        setTranscriptionsAndSolutionsData(currentPageData);
      } else {
        setTranscriptionsAndSolutionsData([]);
      }
    }
  }, [currentPage, groupedDataByPages]);

  const handleEditConfirm = useCallback(
    async ({ transcription, solution }) => {
      seteditOpen(false);
      let solutionMarkdown = solution;

      if (solution['ops'] !== undefined) {
        solutionMarkdown = deltaToMarkdown({ deltaData: solution['ops'] });
      }

      const data = {
        transcription: deltaToMarkdown({ deltaData: transcription['ops'] }),
        solution: solutionMarkdown
      };

      const { success } = await transcriptionsAndSolutionsUpdate({
        objectiveTranscriptionAndSolutionId:
          transcriptionsAndSolutionsData[activeIndex].id,
        data
      });

      if (success) {
        transcriptionsAndSolutionsSendFeedback({
          trackerId: trackerId
        });
        setTranscriptionsAndSolutionsData(null);
        refetchTranscriptionsAndSolutions();
      }
    },
    [
      activeIndex,
      refetchTranscriptionsAndSolutions,
      transcriptionsAndSolutionsData,
      trackerId
    ]
  );

  const handleDelete = async ({ index }) => {
    const { success } = await transcriptionAndSolutionDelete({
      objectiveTranscriptionAndSolutionId:
        transcriptionsAndSolutionsData[index].id
    });

    if (success) {
      setActiveIndex(null);
      setTranscriptionsAndSolutionsData(null);
      refetchTranscriptionsAndSolutions();

      await transcriptionsAndSolutionsSendFeedback({
        trackerId: trackerId
      });
    }
  };

  const handlePageChange = (event) => {
    const selectedPage = pages.find((page) => page.id === event.target.value);
    setCurrentPage(selectedPage);
    setActiveIndex(null);
    setTranscriptionsAndSolutionsData(null);
  };
  return (
    <>
      {!isLoadingTranscriptionsAndSolutions &&
        transcriptionsAndSolutionsData?.length > 0 && (
          <div className={styles.content}>
            {currentPage && pages.length > 1 && (
              <FormControl variant="outlined" style={{ minWidth: 120 }}>
                <InputLabel id="page-select-label">Page</InputLabel>
                <Select
                  labelId="page-select-label"
                  id="page-select"
                  value={currentPage?.id || ''}
                  onChange={handlePageChange}
                  label="Page"
                >
                  {pages.map((page) => (
                    <MenuItem key={page.id} value={page.id}>
                      {page.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <div className={styles.questionsAndAnswersContainer}>
              <div className={styles.questions}>
                <div className={styles.header}>
                  <Typography variant="H-Text-2" color={colors.white}>
                    Questions
                  </Typography>
                </div>
                <div className={styles.questionsList}>
                  {transcriptionsAndSolutionsData?.map(
                    ({ transcription, _ }, index) => (
                      <div
                        key={index}
                        className={cx(styles.question, {
                          [styles.active]: activeIndex === index
                        })}
                        onClick={() => setActiveIndex(index)}
                      >
                        <Typography variant="B-Text-1" color={colors.blue2}>
                          {index + 1}{' '}
                        </Typography>
                        <QuestionField
                          key={activeIndex}
                          initalValue={transcription}
                          readOnlyMode={true}
                          onChange={() => {}}
                        />
                      </div>
                    )
                  )}
                </div>
              </div>
              <div className={styles.answers}>
                <div className={styles.header}>
                  <Typography variant="H-Text-2" color={colors.white}>
                    Answers
                  </Typography>
                </div>
                <div className={styles.activeAnswer}>
                  {activeIndex !== null && (
                    <>
                      <div className={styles.questionField}>
                        <QuestionField
                          key={activeIndex}
                          initalValue={
                            transcriptionsAndSolutionsData[activeIndex].solution
                          }
                          readOnlyMode={true}
                          onChange={() => {}}
                        />
                      </div>
                      <div className={styles.actions}>
                        <IconButton
                          className={styles.deleteButton}
                          onClick={() => handleDelete({ index: activeIndex })}
                        >
                          <DeleteIcon />
                        </IconButton>
                        <IconButton
                          className={styles.deleteButton}
                          onClick={() => {
                            seteditOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      {editOpen && (
        <EditDialog
          open={editOpen}
          onClose={() => seteditOpen(false)}
          answer={transcriptionsAndSolutionsData[activeIndex]?.solution}
          question={transcriptionsAndSolutionsData[activeIndex]?.transcription}
          activeIndex={activeIndex}
          onConfirm={({ transcription, solution }) =>
            handleEditConfirm({ transcription, solution })
          }
        />
      )}
    </>
  );
};

export default TrackerQuestionsAndAnswersList;
